import type { JourneyNode } from "@/lib/journey-api";
import type { JourneyPlanStep } from "@/lib/journey-bindings";
import { handbookOrdinal } from "@/lib/timeline-zones";

const SHANGHAI_TZ = "Asia/Shanghai";
const LIVE_STATUSES = new Set(["planned", "queued"]);

export type ReflowInput = {
  now: Date;
  currentStepId: number;
  selectedAt: Date;
  steps: JourneyPlanStep[];
  nodes: Pick<JourneyNode, "id" | "offset_days">[];
  inFlightStepIds: number[];
  sentStepIds: number[];
};

export type ReflowResult = {
  currentAt: string;
  updates: { id: number; plannedAt: string }[];
};

/** 上海墙钟 date + time → ISO（秒固定 00，+08:00） */
export function shanghaiWallToIso(date: string, time: string): string {
  const [hh, mm] = time.split(":");
  const hour = (hh ?? "00").padStart(2, "0");
  const minute = (mm ?? "00").padStart(2, "0");
  return `${date}T${hour}:${minute}:00+08:00`;
}

/** 快捷档：所选日 + 档点若已 ≤ now，则日期 +1 天 */
export function applySlotShortcut(opts: {
  now: Date;
  selectedDate: string;
  slot: string;
}): { date: string; time: string } {
  const candidateIso = shanghaiWallToIso(opts.selectedDate, opts.slot);
  if (parseIsoToMs(candidateIso) <= opts.now.getTime()) {
    return { date: addCalendarDays(opts.selectedDate, 1), time: opts.slot };
  }
  return { date: opts.selectedDate, time: opts.slot };
}

export function countReflowUpdates(result: ReflowResult): number {
  return result.updates.length;
}

/** 定时改期连坐：按手册 δ 重算后续步 planned_at */
export function reflowPlannedAts(input: ReflowInput): ReflowResult {
  const { now, currentStepId, selectedAt, steps, nodes, inFlightStepIds, sentStepIds } =
    input;

  const currentStep = steps.find((s) => s.id === currentStepId);
  if (!currentStep) {
    return { currentAt: shanghaiWallToIsoFromDate(selectedAt), updates: [] };
  }

  const selectedWall = shanghaiWallFromDate(selectedAt);
  const currentAt = shanghaiWallToIso(selectedWall.date, selectedWall.time);

  const currentNodeId = currentStep.journey_node_id;
  if (currentNodeId == null) {
    return { currentAt, updates: [] };
  }

  const currentNode = nodes.find((n) => n.id === currentNodeId);
  if (!currentNode) {
    return { currentAt, updates: [] };
  }

  const currentOrdinal = handbookOrdinal(nodes, currentNodeId);
  if (currentOrdinal == null) {
    return { currentAt, updates: [] };
  }

  const inFlightSet = new Set(inFlightStepIds);
  const sentSet = new Set(sentStepIds);
  const nowAt = shanghaiWallToIsoFromDate(now);

  const subsequent = steps
    .filter((s) => {
      if (s.id === currentStepId) return false;
      if (!LIVE_STATUSES.has(s.status)) return false;
      if (inFlightSet.has(s.id) || sentSet.has(s.id)) return false;
      const ord = handbookOrdinal(nodes, s.journey_node_id);
      if (ord == null || ord <= currentOrdinal) return false;
      return nodes.some((n) => n.id === s.journey_node_id);
    })
    .sort((a, b) => {
      const oa = handbookOrdinal(nodes, a.journey_node_id) ?? 999;
      const ob = handbookOrdinal(nodes, b.journey_node_id) ?? 999;
      if (oa !== ob) return oa - ob;
      return a.id - b.id;
    });

  const updates: ReflowResult["updates"] = [];
  let prevAt = currentAt;

  for (const step of subsequent) {
    const node = nodes.find((n) => n.id === step.journey_node_id)!;
    const deltaDays = node.offset_days - currentNode.offset_days;
    let date = addCalendarDays(selectedWall.date, deltaDays);
    const tod = todFromPlannedAt(step.planned_at);
    let t = shanghaiWallToIso(date, tod);

    const floor = addOneMinute(maxIso(nowAt, prevAt));
    while (parseIsoToMs(t) <= parseIsoToMs(floor)) {
      date = addCalendarDays(date, 1);
      t = shanghaiWallToIso(date, tod);
    }

    updates.push({ id: step.id, plannedAt: t });
    prevAt = t;
  }

  return { currentAt, updates };
}

function shanghaiWallToIsoFromDate(d: Date): string {
  const wall = shanghaiWallFromDate(d);
  return shanghaiWallToIso(wall.date, wall.time);
}

function shanghaiWallFromDate(d: Date): { date: string; time: string } {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: SHANGHAI_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = fmt.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour").padStart(2, "0")}:${get("minute").padStart(2, "0")}`,
  };
}

/** 日历日加法（Date.UTC，不吃宿主时区） */
function addCalendarDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d + days));
  const yy = utc.getUTCFullYear();
  const mm = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(utc.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function todFromPlannedAt(plannedAt: string | null): string {
  if (!plannedAt) return "10:00";
  return shanghaiWallFromDate(new Date(plannedAt)).time;
}

function parseIsoToMs(iso: string): number {
  return new Date(iso).getTime();
}

function maxIso(a: string, b: string): string {
  return parseIsoToMs(a) >= parseIsoToMs(b) ? a : b;
}

function addOneMinute(iso: string): string {
  const wall = shanghaiWallFromDate(new Date(parseIsoToMs(iso) + 60_000));
  return shanghaiWallToIso(wall.date, wall.time);
}
